import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, DraftingCompass, Rocket, LineChart } from "lucide-react";
import { PageHero, PageCta } from "@/components/PageParts";
import { usePageMeta } from "@/hooks/usePageMeta";

const steps = [
  {
    num: "01",
    icon: Search,
    title: "Discovery & AI Audit",
    duration: "Weeks 1-2",
    description:
      "We map your operations, data and systems to find where intelligence creates the most leverage — and what it is worth. You get a clear picture of the opportunity before any commitment.",
    deliverables: ["Stakeholder interviews and process mapping", "Opportunity scoring with dollar estimates", "Findings deck for your leadership team"],
  },
  {
    num: "02",
    icon: DraftingCompass,
    title: "Architecture & Blueprint",
    duration: "Weeks 3-4",
    description:
      "A concrete technical and financial plan: stack decisions, integration points, timeline and the ROI model behind every milestone. No black boxes — every choice is justified in plain language.",
    deliverables: ["Technical architecture and stack decision record", "Phased delivery plan with owners and dates", "ROI model reviewed against your numbers"],
  },
  {
    num: "03",
    icon: Rocket,
    title: "Agile Implementation",
    duration: "Weeks 5-14",
    description:
      "Senior engineers ship in tight cycles with weekly demos. A working prototype inside 14 days, production systems inside the quarter. You see progress every single week, not at the end.",
    deliverables: ["Working prototype by day 14", "Weekly demos and sprint reviews", "Production deployment with monitoring"],
  },
  {
    num: "04",
    icon: LineChart,
    title: "Scale & ROI Optimization",
    duration: "Ongoing",
    description:
      "We stay through the numbers: monitoring, iteration and growth loops until the projected return is a recorded one. Success is measured against the ROI model from phase two.",
    deliverables: ["Performance dashboards against targets", "Monthly optimization cycles", "Quarterly ROI review with leadership"],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function ProcessPage() {
  usePageMeta(
    "Our Process — May Forward Tech",
    "A disciplined four-phase path from idea to return: discovery, architecture, agile implementation and ROI optimization."
  );
  return (
    <>
      <PageHero
        eyebrow="How we work"
        title="A disciplined path from idea to return"
        intro="Four phases. No black boxes, no endless discovery theatre — and progress you can see every week."
        image="/img/process-lines.png"
      />
      <section className="py-16 lg:py-24 bg-[#FAFAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute left-[27px] top-4 bottom-4 w-[2px] bg-gradient-to-b from-[#0FA89E] via-[rgba(15,168,158,0.3)] to-transparent hidden sm:block" />
            <div className="space-y-10">
              {steps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.num}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                    data-testid={`process-phase-${i + 1}`}
                    className="relative sm:pl-24"
                  >
                    <div className="hidden sm:flex absolute left-0 top-1 w-14 h-14 rounded-2xl bg-white border border-[rgba(15,168,158,0.3)] items-center justify-center z-10">
                      <Icon className="w-6 h-6 text-[#0FA89E]" />
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-7 lg:p-9 transition-all duration-300 hover:border-[rgba(15,168,158,0.4)] hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.12)]">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="font-mono-tech text-sm font-semibold text-[#F5A400]">{step.num}</span>
                        <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0F172A]">
                          {step.title}
                        </h2>
                        <span className="ml-auto font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0FA89E] border border-[rgba(15,168,158,0.25)] rounded-full px-3 py-1">
                          {step.duration}
                        </span>
                      </div>
                      <p className="text-base text-slate-600 leading-relaxed mb-6 max-w-2xl">
                        {step.description}
                      </p>
                      <div>
                        <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">
                          What leaves this phase
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {step.deliverables.map((d, j) => (
                            <li key={j} className="rounded-xl bg-[#FAFAFC] border border-slate-100 px-4 py-3 text-sm text-slate-700">
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <PageCta />
    </>
  );
}
